import { Ear, Lightbulb, Compass, Sparkles } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const PRINCIPLES = [
    {
        number: "01",
        title: "Ascolto",
        subtitle: "Entrare nella tua storia",
        text: "Prima di cercare risposte, bisogna capire la domanda. Ascoltare significa dare spazio a ciò che una persona porta in terapia, anche quando è confuso, difficile da raccontare o ancora senza un significato preciso.",
        Icon: Ear,
    },
    {
        number: "02",
        title: "Comprensione",
        subtitle: "Dare un significato a ciò che accade",
        text: "Emozioni, pensieri, corpo, comportamenti e relazioni non sono elementi separati. Comprenderne i legami permette di vedere con maggiore chiarezza ciò che accade e riconoscere quei meccanismi che spesso si attivano senza che ce ne rendiamo conto.",
        Icon: Lightbulb,
    },
    {
        number: "03",
        title: "Consapevolezza",
        subtitle: "Accorgersi di ciò che prima sfuggiva",
        text: "La consapevolezza non significa controllare tutto. Significa riconoscere meglio ciò che sentiamo, ciò di cui abbiamo bisogno, ciò che ci condiziona e il modo in cui reagiamo.",
        Icon: Compass,
    },
    {
        number: "04",
        title: "Cambiamento",
        subtitle: "Trasformare la comprensione in possibilità",
        text: "Capire è importante, ma non è il punto di arrivo. Il percorso terapeutico porta gradualmente a sperimentare modi diversi di affrontare emozioni, pensieri, relazioni e situazioni, fino a poter scegliere con maggiore libertà come stare nelle proprie esperienze.",
        Icon: Sparkles,
    },
];

export const Approccio = () => (
    <section id="approccio" className="bg-cream" data-testid="approccio-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 md:py-32">
            <Reveal className="max-w-3xl">
                <Eyebrow>L'approccio</Eyebrow>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-snug text-ink" data-testid="approccio-title">
                    Il mio modo di lavorare
                </h2>
                <div className="mt-7 space-y-4 text-base sm:text-lg text-[#526063] leading-relaxed">
                    <p className="text-ink font-medium">La psicoterapia non è un percorso uguale per tutti.</p>
                    <p>
                        Parto dalla persona, dalla sua storia e dal modo particolare in cui vive ciò che sta attraversando. Il lavoro terapeutico consiste nel comprendere insieme cosa accade, come si è costruito e cosa può essere modificato.
                    </p>
                </div>
            </Reveal>

            <div className="mt-14 grid sm:grid-cols-2 gap-6 lg:gap-8">
                {PRINCIPLES.map((p, i) => (
                    <Reveal key={p.number} delay={i * 0.1}>
                        <article
                            className="group h-full rounded-3xl bg-white p-8 sm:p-10 border border-ink/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
                            data-testid={`approccio-card-${i}`}
                        >
                            <div className="flex items-start justify-between">
                                <span className="font-serif text-5xl text-sage/40 group-hover:text-sage/70 transition-colors duration-500">
                                    {p.number}
                                </span>
                                <span className="rounded-full bg-sage-light p-3 text-sage-dark">
                                    <p.Icon size={22} strokeWidth={1.6} />
                                </span>
                            </div>
                            <h3 className="mt-6 font-serif text-2xl font-semibold text-ink">{p.title}</h3>
                            <p className="mt-1 text-sm font-semibold uppercase tracking-[0.12em] text-terracotta">{p.subtitle}</p>
                            <p className="mt-3 text-base text-[#526063] leading-relaxed">{p.text}</p>
                        </article>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.2}>
                <p className="mt-14 max-w-2xl mx-auto text-center font-serif italic text-xl sm:text-2xl text-petrol leading-relaxed" data-testid="approccio-closing">
                    "Ogni percorso è costruito sulla persona, non sulla semplice applicazione di una tecnica."
                </p>
            </Reveal>
        </div>
    </section>
);
